import { Fragment, useEffect, useRef, useState, type CSSProperties } from 'react';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import { Tooltip } from '../../atoms/Tooltip';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import styles from './IconSidebar.module.scss';
import type { IconSidebarItem, IconSidebarProps } from './IconSidebar.types';

function itemMatchesActivePath(
  activePath: string,
  item: Pick<IconSidebarItem, 'id' | 'path'>
): boolean {
  return (
    activePath === item.path ||
    activePath === item.id ||
    activePath.startsWith(`${item.path}/`)
  );
}

/**
 * IconSidebar - Vertical sidebar with icon-only navigation
 * Always mounted in StudentAreaWrapper — never unmounts across route changes.
 * On mobile: hides on scroll down, shows on scroll up (Framer Motion).
 * hideOnMobile=true pushes it off-screen (story/resource detail pages).
 * Si se pasan `moreItems`, en móvil añade un botón "Más" (⋯) que expande la propia
 * navbar en sitio a una 2ª fila con esos accesos (sin diálogo/drawer).
 */
export function IconSidebar({
  items,
  activePath,
  onNavigate,
  className,
  hideOnMobile,
  moreItems,
  moreLabel,
  moreNavOrder,
}: IconSidebarProps) {
  const controls = useAnimationControls();
  const prevHideRef = useRef(hideOnMobile);
  const { direction, isMobile, hasScrolledPastHideThreshold, reset } = useScrollDirection();
  const isVisible = !hasScrolledPastHideThreshold || direction !== 'down';
  const [expanded, setExpanded] = useState(false);
  // Mientras está expandida no se oculta por scroll (mobileY=0).
  const mobileY = hideOnMobile ? 150 : expanded ? 0 : isVisible ? 0 : 150;

  const hasMore = isMobile && !!moreItems && moreItems.length > 0;

  // ¿La ruta actual corresponde a un item del panel ⋯ (p. ej. Perfil/Ajustes)?
  const matchesPath = (path?: string) =>
    !!path && (activePath === path || activePath.startsWith(`${path}/`));
  // El botón ⋯ se marca activo cuando estás en una de sus páginas.
  const moreActive = (moreItems ?? []).some((m) => matchesPath(m.path));

  // Orden de la fila de navegación del panel ampliado (independiente de la barra
  // cerrada). Los ids listados primero en su orden; el resto se añade al final.
  const expandedNavItems = moreNavOrder
    ? [
        ...moreNavOrder
          .map((id) => items.find((i) => i.id === id))
          .filter((i): i is IconSidebarItem => !!i),
        ...items.filter((i) => !moreNavOrder.includes(i.id)),
      ]
    : items;

  /** Evita parpadeo violeta al soltar :active antes de que activePath actualice la ruta. */
  const [transitionalPressPath, setTransitionalPressPath] = useState<string | null>(null);

  // Mobile: si hay un orden explícito (moreNavOrder) la barra cerrada lo respeta
  // (igual que el panel ampliado); si no, centra "Stories". Desktop: por defecto.
  const orderedItems = isMobile
    ? moreNavOrder
      ? expandedNavItems
      : (() => {
          const cIdx = items.findIndex((i) => i.id === 'stories');
          if (cIdx === -1) return items;
          const arr = [...items];
          const midIdx = Math.floor(arr.length / 2);
          if (cIdx === midIdx) return arr;
          const cItem = arr[cIdx];
          if (cItem == null) return arr;
          arr.splice(cIdx, 1);
          arr.splice(midIdx, 0, cItem);
          return arr;
        })()
    : items;

  // Animate vertical hide/show on mobile (track is full-width flex; bar width = content)
  useEffect(() => {
    if (!isMobile) return;
    void controls.start({ y: mobileY, transition: { type: 'spring', stiffness: 200, damping: 25 } });
  }, [mobileY, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps -- controls is a stable Framer Motion object; its identity never changes

  // When returning from a full-screen route: immediately show sidebar and reset scroll state.
  // controls.set() is synchronous — avoids race conditions with scroll events.
  useEffect(() => {
    if (prevHideRef.current && !hideOnMobile && isMobile) {
      controls.set({ y: 0 });
      reset();
    }
    prevHideRef.current = hideOnMobile;
  }, [hideOnMobile, isMobile, controls, reset]);

  // Cerrar el panel ampliado si la navbar se oculta (entrar en historia, etc.).
  useEffect(() => {
    if (hideOnMobile) setExpanded(false);
  }, [hideOnMobile]);

  // Al navegar desde el panel ampliado NO lo cerramos en el tap (evita que la
  // animación de cierre se solape con la cortina violeta). Como las navegaciones
  // /student pasan por el blocker, la ruta cambia cuando la cortina ya cubre la
  // pantalla; colapsamos aquí, ya oculto tras el overlay.
  const prevActivePathRef = useRef(activePath);
  useEffect(() => {
    if (prevActivePathRef.current !== activePath) {
      prevActivePathRef.current = activePath;
      setExpanded(false);
    }
  }, [activePath]);

  // Navegación desde el panel: si el destino ya es la página actual no hay cortina,
  // así que cerramos al momento; si no, dejamos el panel como está (lo tapa la
  // cortina y el efecto anterior lo colapsa tras el cambio de ruta).
  const navigateFromPanel = (run: () => void, isActive: boolean) => {
    if (isActive) {
      setExpanded(false);
      return;
    }
    run();
  };

  // Cerrar con Escape mientras está expandida.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  useEffect(() => {
    if (transitionalPressPath == null) return;
    const target = orderedItems.find((i) => i.path === transitionalPressPath);
    if (target == null) {
      setTransitionalPressPath(null);
      return;
    }
    if (itemMatchesActivePath(activePath, target)) {
      setTransitionalPressPath(null);
    }
  }, [activePath, transitionalPressPath, orderedItems]);

  useEffect(() => {
    if (transitionalPressPath == null) return;
    const id = window.setTimeout(() => setTransitionalPressPath(null), 600);
    return () => window.clearTimeout(id);
  }, [transitionalPressPath]);

  const asideClassName = cn(
    styles.sidebar,
    hideOnMobile && styles['sidebar--hideOnMobile'],
    className
  );

  const nav = (
    <nav
      className={cn(
        styles.sidebar__nav,
        transitionalPressPath != null && styles['sidebar__nav--transitionalPress']
      )}
    >
      {orderedItems.map((item) => {
        const isActive = itemMatchesActivePath(activePath, item);
        const isPressTarget = transitionalPressPath === item.path;
        const button = (
          <button
            className={cn(
              styles.sidebar__item,
              isActive && styles['sidebar__item--active'],
              isPressTarget && styles['sidebar__item--pressTarget']
            )}
            onPointerDown={(e) => {
              if (e.button !== 0 || isActive) return;
              setTransitionalPressPath(item.path);
            }}
            onClick={() => onNavigate(item.path)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon icon={item.icon} size="md" />
          </button>
        );

        return isMobile ? (
          <Fragment key={item.id}>{button}</Fragment>
        ) : (
          <Tooltip key={item.id} content={item.label} side="right" showArrow={false}>
            {button}
          </Tooltip>
        );
      })}

      {/* Solo móvil: botón "Más" (⋯) al final → expande la navbar en sitio. */}
      {hasMore && (
        <button
          type="button"
          className={styles.sidebar__item}
          onClick={() => setExpanded(true)}
          aria-label={moreLabel}
          aria-expanded={expanded}
        >
          <Icon icon={MoreHorizontal} size="md" />
          {/* Estás en una página de la 2ª fila → solo un punto arriba a la derecha
              (como el del avatar con notificaciones), sin estado seleccionado. */}
          {moreActive && <span className={styles.sidebar__moreDot} aria-hidden />}
        </button>
      )}
    </nav>
  );

  if (isMobile) {
    return (
      <>
        {/* Captura el tap fuera para cerrar (sin atenuar: no es un drawer). */}
        <AnimatePresence>
          {expanded && hasMore && (
            <motion.div
              key="more-backdrop"
              className={styles.sidebarBackdrop}
              aria-hidden
              onClick={() => setExpanded(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            />
          )}
        </AnimatePresence>

        <motion.div
          className={styles.sidebarMobileTrack}
          // Empezamos en la posición final correcta para evitar que la barra
          // se vea visible un frame y luego se deslice fuera al refrescar en
          // rutas full-screen (story / conversación).
          initial={{ y: mobileY }}
          animate={controls}
          transition={{ type: 'spring', stiffness: 200, damping: 25 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {expanded && hasMore ? (
              <motion.aside
                key="expanded"
                className={cn(styles.sidebar, styles['sidebar--expanded'])}
                // Nº de botones de la barra cerrada (items + ⋯): fija el ancho de
                // las filas para que el panel abierto mida igual que el cerrado.
                style={{ '--nav-count': orderedItems.length + 1 } as CSSProperties}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18, ease: [0.42, 0, 0.58, 1] }}
              >
                <div className={styles.sidebar__expandedRow}>
                  {expandedNavItems.map((item) => {
                    const isActive = itemMatchesActivePath(activePath, item);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={cn(
                          styles.sidebar__expandedItem,
                          isActive && styles['sidebar__expandedItem--active']
                        )}
                        onClick={() => navigateFromPanel(() => onNavigate(item.path), isActive)}
                        aria-label={item.label}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <Icon icon={item.icon} size="md" />
                      </button>
                    );
                  })}
                </div>
                <div className={styles.sidebar__expandedRow}>
                  {moreItems.map((m) => {
                    const active = matchesPath(m.path);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className={cn(
                          styles.sidebar__expandedItem,
                          active && styles['sidebar__expandedItem--active']
                        )}
                        onClick={() => navigateFromPanel(m.onClick, active)}
                        aria-label={m.label}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon icon={m.icon} size="md" />
                      </button>
                    );
                  })}
                </div>
              </motion.aside>
            ) : (
              <aside key="collapsed" className={asideClassName}>
                {nav}
              </aside>
            )}
          </AnimatePresence>
        </motion.div>
      </>
    );
  }

  return <aside className={asideClassName}>{nav}</aside>;
}

IconSidebar.displayName = 'IconSidebar';
